import "../../global.css";

import "@/client/css/global.scss";
import "rc-tooltip/assets/bootstrap.css";
import "@/client/css/nativebase.scss";

import {
    AppProvider,
    FrontendProvider,
    ToastProvider,
    ToastSetter,
    useWindowSize
} from "@hosttools/frontend";
import {Box} from "@hosttools/frontend/components";
import {GluestackUIProvider} from "@hosttools/frontend/components/ui/gluestack-ui-provider";
import {ErrorBoundary} from "@sentry/react";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {polyfillCountryFlagEmojis} from "country-flag-emoji-polyfill";
import FontFaceObserver from "fontfaceobserver";
import type {FC} from "react";
import {lazy, memo, Suspense, useEffect, useMemo, useState} from "react";
import FullStory from "react-fullstory";
import {BrowserRouter, Redirect, Switch} from "react-router-dom";

import Loader from "./components/Loader";
import MessagesNotification from "./components/MessagesNotification";
import {
    INBOX_PATH,
    MESSAGING_PATH,
    MULTICALENDAR_PATH,
    PRICING_PATH,
    SETTINGS_PATH
} from "./constant";
import Error from "./Error";
import PrivateRoute from "./PrivateRoute";
import ErrorProvider from "./providers/ErrorProvider";
import PublicRoute from "./PublicRoute";

import AddListing from "@/client/features/AddListing";
import ConnectListing from "@/client/features/ConnectListing";

const ORG_ID = __IS_BETA__ ? "o-1HJ52A-na1" : "o-1HC3P9-na1";

const MultiCalendar = lazy(() => import("./components/MultiCalendar"));
const Turnovers = lazy(() => import("./components/Turnovers"));
const MessageRuleRoute = lazy(() => import("../client/components/MessageRuleRoute"));
const Pricing = lazy(() => import("./components/Pricing"));
const Settings = lazy(() => import("../client/components/Settings"));
const Inbox = lazy(() => import("./components/Inbox"));
const Register = lazy(() => import("../client/components/Register"));
const Forgot = lazy(() => import("../client/components/Forgot"));
const Login = lazy(() => import("../client/components/Login"));
const Reset = lazy(() => import("../client/components/Reset"));
const QuickGuide = lazy(() => import("../client/features/QuickGuide"));
const ListingContent = lazy(() => import("../client/features/ListingContent"));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1
        }
    }
});

const Root: FC = () => {
    const size = useWindowSize({
        delay: 350,
        onResize() {
            // trick to deal with iOS Safari toolbar
            // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        }
    });

    return (
        <Box
            // this requires to work on mobile browsers especially Safari on iOS
            // which doesn't work great with `100vh` due to its toolbar
            // but it appears to work with `window.innerHeight`
            className="overflow-y-auto overflow-x-hidden bg-background-0"
            style={{
                height: size ? `${size.height}px` : "100vh"
            }}
            testID="root-container"
        >
            <Suspense fallback={<Loader center />}>
                <Switch>
                    <PrivateRoute exact component={MultiCalendar} path={MULTICALENDAR_PATH} />
                    <PrivateRoute
                        component={Inbox}
                        path={INBOX_PATH}
                        permission={useMemo(() => ["inbox", "edit"], [])}
                    />
                    <PrivateRoute
                        component={MessageRuleRoute}
                        path={MESSAGING_PATH}
                        permission={useMemo(() => ["messageRules", "edit"], [])}
                    />
                    <PrivateRoute
                        component={Pricing}
                        path={PRICING_PATH}
                        permission={useMemo(() => ["pricing", "edit"], [])}
                    />
                    <PrivateRoute component={Settings} path={SETTINGS_PATH} />
                    <PrivateRoute component={QuickGuide} path="/quick-guide" />
                    <PrivateRoute
                        component={ConnectListing}
                        path="/connect/:listingID/:accountType"
                    />
                    <PrivateRoute component={AddListing} path="/add-listing" />
                    <PrivateRoute component={ListingContent} path="/listing/:listingID" />

                    <PublicRoute redirect component={Login} path="/login" />
                    <PublicRoute redirect component={Register} path="/register" />
                    <PublicRoute component={Forgot} path="/forgot" />
                    <PublicRoute component={Reset} path="/reset/:resetPasswordToken" />
                    <PublicRoute component={Turnovers} path="/turnovers/:accessCode" />
                    <Redirect to="/" />
                </Switch>
            </Suspense>
            <MessagesNotification />
        </Box>
    );
};

const App: FC = () => {
    const [fontLoaded, setFontLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            const font = new FontFaceObserver("InterDisplay", {
                // keep in mind, this must be 500 which is the `medium` being set on `Text` component
                weight: 500
            });
            try {
                await font.load();
                setFontLoaded(true);
            } catch {
                setFontLoaded(true);
            }
        })();
        polyfillCountryFlagEmojis("TwemojiMozilla");
    }, []);

    if (!fontLoaded) {
        // we just simply allow blank screen until font is loaded
        return null;
    }

    return (
        <ErrorBoundary fallback={<Error />}>
            <QueryClientProvider client={queryClient}>
                {/* this should wrap nativebase otherwise modal/drawer/menu components can't read data from UserContext */}
                <FrontendProvider baseUrl="">
                    <AppProvider>
                        <ToastProvider>
                            <BrowserRouter>
                                <GluestackUIProvider mode="light">
                                    <ErrorProvider>
                                        {!__DEV__ && <FullStory org={ORG_ID} />}
                                        <Root />
                                        <ToastSetter />
                                    </ErrorProvider>
                                </GluestackUIProvider>
                            </BrowserRouter>
                        </ToastProvider>
                    </AppProvider>
                </FrontendProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    );
};

export default memo(App);
